import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { KTSVG, toAbsoluteUrl } from '@helpers/index';
import {
  getDefaultSubscriptionSelector,
  getPetSelector,
} from '@modules/subscriptions/core/selectors';
import { useSubscriptions } from '@modules/subscriptions/hooks/useSubscriptions';

type SubscriptionsListItemProps = {
  subscription: ISubscription;
  isSelected: boolean;
};

export const SubscriptionsListItem = ({ subscription, isSelected }: SubscriptionsListItemProps) => {
  const intl = useIntl();
  const pet = useSelector(getPetSelector);

  const { switchDefaultSubscription } = useSubscriptions();

  const switchSubscription = (subscription: ISubscription) =>
    switchDefaultSubscription(subscription);

  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);

  const todaysYear = new Date().getFullYear();

  const birthYear = subscription?.pet?.birth_year ?? todaysYear;
  const birthMonth = subscription?.pet?.birth_month ?? 1;
  const months = (todaysYear - birthYear) * 12 + new Date().getMonth() + 1 - birthMonth;
  const years = Math.floor(months / 12);

  // This item renders its OWN subscription's pet, not the default one.
  const petBreeds = subscription?.pet?.breed_labels ?? subscription?.pet?.breed;
  const breed = petBreeds?.[0] || intl.formatMessage({ id: 'PET.BREED_FALLBACK' });

  return (
    <div className="subscription-switcher">
      <span
        className={clsx('subscription-switcher-status', {
          '!opacity-0 ': subscription?.status === 'ACTIVE',
          '!text-light-yellow': subscription?.status === 'PAUSED',
        })}>
        {subscription?.status === 'CANCELLED' ? (
          <FormattedMessage id="SUBSCRIPTION.STATUS.CANCELLED" />
        ) : subscription?.status === 'PAUSED' ? (
          <FormattedMessage id="SUBSCRIPTION.STATUS.PAUSED" />
        ) : (
          '.'
        )}
      </span>
      <button
        onClick={() => switchSubscription(subscription)}
        className={clsx('subscription-switcher-button', {
          'border border-primary !shadow-custom': subscription?.id === defaultSubscription?.id,
          '!border-light-400': subscription?.status === 'CANCELLED',
          '!border-light-yellow': subscription?.status === 'PAUSED',
        })}>
        <div className="subscription-switcher-item-wrapper">
          <div
            className={clsx(
              'subscription-switcher-item',
              {
                'opacity-50': subscription?.id !== defaultSubscription?.id,
              },
              { '!bg-light-400 !opacity-100': subscription?.status === 'CANCELLED' },

              { '!bg-light-yellow !opacity-100': subscription?.status === 'PAUSED' },
            )}>
            <div className="subscription-switcher-gender">
              {pet?.gender === 'm' ? (
                <KTSVG path={toAbsoluteUrl('/media/svg/pet/gender-m-white.svg')} />
              ) : (
                <KTSVG path={toAbsoluteUrl('/media/svg/pet/gender-f-white.svg')} />
              )}
            </div>
            <div className="subscription-switcher-name">{subscription?.pet?.name ?? '-'}</div>
          </div>
          <div
            className={clsx('text-primary subscription-pet-year', {
              '!text-light-400': subscription?.status === 'CANCELLED',
              '!text-light-yellow': subscription?.status === 'PAUSED',
            })}>
            <div className="subscription-pet-year-item">
              <KTSVG path={toAbsoluteUrl('/media/svg/pet/briefcase.svg')} />
              <p>{years} Jahre</p>
            </div>
            <div className="subscription-pet-year-item">
              <KTSVG path={toAbsoluteUrl('/media/svg/pet/paw.svg')} />
              {/* `.subscription-pet-breed` already clips with nowrap + ellipsis,
                  so the name is rendered whole. It used to have its spaces
                  stripped, which was invisible while every name was German and
                  one word but turns "Berger allemand" into "Bergerallemand". */}
              <p className="subscription-pet-breed">{breed}</p>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};
